import { useEffect, useState } from "react";
import { Trash2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const statuses = ["new", "contacted", "closed"];

const AdminInquiries = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/inquiries");
            setItems(data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const updateStatus = async (id, status) => {
        try {
            await api.put(`/inquiries/${id}/status`, { status });
            setItems((arr) => arr.map((i) => i.id === id ? { ...i, status } : i));
            toast.success("Status updated");
        } catch {
            toast.error("Update failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this inquiry?")) return;
        try {
            await api.delete(`/inquiries/${id}`);
            setItems((arr) => arr.filter((i) => i.id !== id));
            toast.success("Deleted");
        } catch {
            toast.error("Delete failed");
        }
    };

    return (
        <div data-testid="admin-inquiries-page">
            <div className="overline text-red-600 mb-3">Inbox</div>
            <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-10">Inquiries</h1>

            <div className="bg-white border border-gray-200">
                <div className="grid grid-cols-12 gap-4 p-5 border-b border-gray-200 font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">
                    <div className="col-span-3">Name / Email</div>
                    <div className="col-span-2">Company</div>
                    <div className="col-span-3">Machine</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-1">Date</div>
                    <div className="col-span-1 text-right">Actions</div>
                </div>

                {loading ? (
                    <div className="p-10 text-sm text-gray-500">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="p-10 text-sm text-gray-500">No inquiries yet.</div>
                ) : items.map((inq) => (
                    <div key={inq.id} data-testid={`inquiry-row-${inq.id}`}>
                        <div className="grid grid-cols-12 gap-4 p-5 border-b border-gray-100 items-center text-sm">
                            <div className="col-span-3">
                                <button onClick={() => setOpen(open === inq.id ? null : inq.id)} className="text-left">
                                    <div className="font-medium hover:text-red-600" data-testid={`inquiry-name-${inq.id}`}>{inq.name}</div>
                                    <div className="text-xs text-gray-500">{inq.email}</div>
                                </button>
                            </div>
                            <div className="col-span-2 text-gray-600">{inq.company || "—"}</div>
                            <div className="col-span-3 text-gray-600 truncate">{inq.machine_type || "—"}</div>
                            <div className="col-span-2">
                                <select
                                    value={inq.status}
                                    onChange={(e) => updateStatus(inq.id, e.target.value)}
                                    className="input-field py-2"
                                    data-testid={`inquiry-status-${inq.id}`}
                                >
                                    {statuses.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                                </select>
                            </div>
                            <div className="col-span-1 text-xs text-gray-500">{new Date(inq.created_at).toLocaleDateString()}</div>
                            <div className="col-span-1 flex justify-end">
                                <button onClick={() => remove(inq.id)} className="p-2 text-red-600 hover:bg-red-50" data-testid={`inquiry-delete-${inq.id}`}><Trash2 size={16} /></button>
                            </div>
                        </div>
                        {open === inq.id && (
                            <div className="bg-gray-50 px-5 py-6 border-b border-gray-200 grid md:grid-cols-3 gap-6 text-sm" data-testid={`inquiry-detail-${inq.id}`}>
                                <div>
                                    <div className="overline text-red-600 mb-2">Contact</div>
                                    <div className="space-y-1">
                                        <a className="block hover:text-red-600" href={`mailto:${inq.email}`}><Mail size={12} className="inline mr-1" /> {inq.email}</a>
                                        {inq.phone && <a className="block hover:text-red-600" href={`tel:${inq.phone}`}><Phone size={12} className="inline mr-1" /> {inq.phone}</a>}
                                    </div>
                                </div>
                                <div>
                                    <div className="overline text-red-600 mb-2">Production</div>
                                    <div className="text-gray-700">{inq.production_volume || "—"}</div>
                                </div>
                                <div className="md:col-span-3">
                                    <div className="overline text-red-600 mb-2">Message</div>
                                    <p className="text-gray-700 whitespace-pre-line">{inq.message}</p>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AdminInquiries;
